import { useEffect, useState } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import {
  generateTimeSeriesData,
  generateWeeklyData,
  generateMonthlyData,
  generateCategoryData,
  generateGoalsData,
  generateProductivityMetrics,
  getMetricsComparison
} from "../utils/testDataGenerator";
import "./Analytics.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function AnalyticsDashboard() {
  const [timeframe, setTimeframe] = useState("daily"); // daily, weekly, monthly
  const [dailyData, setDailyData] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [goalsData, setGoalsData] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    setDailyData(generateTimeSeriesData(90));
    setWeeklyData(generateWeeklyData(12));
    setMonthlyData(generateMonthlyData(12));
    setCategoryData(generateCategoryData());
    setGoalsData(generateGoalsData());
    setMetrics(generateProductivityMetrics());
    setComparison(getMetricsComparison());
  }, []);

  // Prepare data based on selected timeframe
  const currentData =
    timeframe === "daily" ? dailyData :
    timeframe === "weekly" ? weeklyData :
    monthlyData;

  // Time series chart configuration
  const timeSeriesChart = {
    labels: currentData.map(d => d.date || d.week || d.month),
    datasets: [
      {
        label: "Hours Logged",
        data: currentData.map(d => d.hours),
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        yAxisID: "y"
      },
      {
        label: "Completion Rate %",
        data: currentData.map(d => d.completion || d.quality || d.growth),
        borderColor: "rgb(255, 193, 7)",
        backgroundColor: "rgba(255, 193, 7, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        yAxisID: "y1"
      }
    ]
  };

  // Task completion chart
  const tasksChart = {
    labels: currentData.map(d => d.date || d.week || d.month),
    datasets: [
      {
        label: "Tasks Completed",
        data: currentData.map(d => d.tasks || d.tasksCompleted || d.goals),
        backgroundColor: "rgba(76, 175, 80, 0.7)",
        borderColor: "rgb(76, 175, 80)",
        borderWidth: 1
      }
    ]
  };

  // Category distribution chart
  const categoryChart = {
    labels: categoryData.map(c => c.category),
    datasets: [
      {
        label: "Hours by Category",
        data: categoryData.map(c => c.hours),
        backgroundColor: [
          "rgba(255, 99, 132, 0.7)",
          "rgba(54, 162, 235, 0.7)",
          "rgba(255, 206, 86, 0.7)",
          "rgba(75, 192, 192, 0.7)",
          "rgba(153, 102, 255, 0.7)"
        ],
        borderColor: [
          "rgb(255, 99, 132)",
          "rgb(54, 162, 235)",
          "rgb(255, 206, 86)",
          "rgb(75, 192, 192)",
          "rgb(153, 102, 255)"
        ],
        borderWidth: 2
      }
    ]
  };

  // Goals progress chart
  const goalsChart = {
    labels: goalsData.map(g => g.title),
    datasets: [
      {
        label: "Progress %",
        data: goalsData.map(g => g.progress),
        backgroundColor: "rgba(103, 58, 183, 0.7)",
        borderColor: "rgb(103, 58, 183)",
        borderWidth: 2
      }
    ]
  };

  // Productivity metrics chart
  const productivityChart = {
    labels: ["Tasks", "Completion", "Efficiency", "Focus"],
    datasets: [
      {
        label: "Score",
        data: [
          (142 / 200) * 100,
          metrics?.completionRate || 0,
          metrics?.efficiency || 0,
          metrics?.focusScore || 0
        ],
        borderColor: "rgb(244, 67, 54)",
        backgroundColor: "rgba(244, 67, 54, 0.1)",
        borderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { usePointStyle: true, padding: 15 }
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13 }
      }
    }
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>📊 Analytics Dashboard</h1>
        <p>Comprehensive progress tracking and insights</p>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-icon">⏱️</div>
            <div className="metric-value">{metrics.totalHours}h</div>
            <div className="metric-label">Total Hours</div>
            <div className="metric-subtext">Last 90 days</div>
          </div>
          <div className="metric-card">
            <div className="metric-icon">📈</div>
            <div className="metric-value">{metrics.averageDaily}</div>
            <div className="metric-label">Hours/Day Avg</div>
            <div className="metric-subtext">Daily average</div>
          </div>
          <div className="metric-card">
            <div className="metric-icon">✅</div>
            <div className="metric-value">{metrics.tasksCompleted}</div>
            <div className="metric-label">Tasks Completed</div>
            <div className="metric-subtext">This period</div>
          </div>
          <div className="metric-card">
            <div className="metric-icon">🎯</div>
            <div className="metric-value">{metrics.completionRate}%</div>
            <div className="metric-label">Completion Rate</div>
            <div className="metric-subtext">Overall progress</div>
          </div>
          <div className="metric-card">
            <div className="metric-icon">🔥</div>
            <div className="metric-value">{metrics.streak}</div>
            <div className="metric-label">Day Streak</div>
            <div className="metric-subtext">Keep it going!</div>
          </div>
          <div className="metric-card">
            <div className="metric-icon">⚡</div>
            <div className="metric-value">{metrics.efficiency}%</div>
            <div className="metric-label">Efficiency Score</div>
            <div className="metric-subtext">Work quality</div>
          </div>
        </div>
      )}

      {/* Timeframe Selector */}
      <div className="timeframe-selector">
        <button
          className={timeframe === "daily" ? "active" : ""}
          onClick={() => setTimeframe("daily")}
        >
          Daily
        </button>
        <button
          className={timeframe === "weekly" ? "active" : ""}
          onClick={() => setTimeframe("weekly")}
        >
          Weekly
        </button>
        <button
          className={timeframe === "monthly" ? "active" : ""}
          onClick={() => setTimeframe("monthly")}
        >
          Monthly
        </button>
      </div>

      {/* Time Series Charts */}
      <div className="charts-grid">
        <div className="chart-container large">
          <h2>Hours & Completion Rate</h2>
          <Line
            data={timeSeriesChart}
            options={{
              ...chartOptions,
              scales: {
                y: {
                  type: "linear",
                  display: true,
                  position: "left",
                  title: { display: true, text: "Hours" }
                },
                y1: {
                  type: "linear",
                  display: true,
                  position: "right",
                  title: { display: true, text: "Completion %" },
                  grid: { drawOnChartArea: false }
                }
              }
            }}
          />
        </div>

        <div className="chart-container">
          <h2>Tasks Completed</h2>
          <Bar data={tasksChart} options={chartOptions} />
        </div>

        <div className="chart-container">
          <h2>Time by Category</h2>
          <Doughnut data={categoryChart} options={chartOptions} />
        </div>

        <div className="chart-container">
          <h2>Goals Progress</h2>
          <Bar data={goalsChart} options={chartOptions} />
        </div>

        <div className="chart-container">
          <h2>Productivity Metrics</h2>
          <Line
            data={productivityChart}
            options={{
              ...chartOptions,
              scales: {
                r: {
                  beginAtZero: true,
                  max: 100
                }
              }
            }}
          />
        </div>
      </div>

      {/* Goals List */}
      <div className="goals-section">
        <h2>Active Goals</h2>
        <div className="goals-list">
          {goalsData.map(goal => (
            <div key={goal.id} className="goal-item">
              <div className="goal-header">
                <h3>{goal.title}</h3>
                <span className="goal-progress">{goal.progress}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${goal.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Comparison */}
      {comparison && (
        <div className="comparison-section">
          <h2>Period Comparison</h2>
          <div className="comparison-cards">
            <div className="comparison-card">
              <h3>Current Period</h3>
              <div className="comparison-value">{comparison.current}</div>
            </div>
            <div className="comparison-card">
              <h3>Previous Period</h3>
              <div className="comparison-value">{comparison.previous}</div>
            </div>
            <div className={`comparison-card ${comparison.trend}`}>
              <h3>Change</h3>
              <div className="comparison-value">
                {comparison.trend === "up" ? "📈" : "📉"} {comparison.change}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insights Section */}
      <div className="insights-section">
        <h2>📌 Key Insights</h2>
        <ul className="insights-list">
          <li>✨ Your most productive day was {currentData.length > 0 ? "last Tuesday" : "N/A"}</li>
          <li>📊 Average productivity is {metrics?.efficiency || 0}% across all categories</li>
          <li>🎯 You're on track to complete {goalsData.filter(g => g.progress >= 75).length} goals this period</li>
          <li>⚡ Peak performance hours: 9-11 AM and 2-4 PM</li>
          <li>💡 Focus on Development category for maximum impact</li>
        </ul>
      </div>
    </div>
  );
}
